import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getCourseCompletion, GetCourseCompletionOutputType, signDeclaration, generateTrainingCertificate } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle, Circle, Award, FileSignature, Download, Shield, AlertTriangle, Loader2, ExternalLink } from 'lucide-react';

type Data = GetCourseCompletionOutputType;

function ChecklistItem({ item }: { item: Data['checklist'][0] }) {
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${item.completed ? 'border-primary/20 bg-primary/5' : 'border-border bg-card'}`}>
      {item.completed ? <CheckCircle className="w-5 h-5 text-primary shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{item.label}</p>
        <p className="text-xs text-muted-foreground">{item.detail}</p>
      </div>
    </div>
  );
}

function DeclarationCard({ type, declaration, canSign, declarationText, onSign, signing }: {
  type: 'Employee' | 'Manager';
  declaration: Data['declarations']['employee'];
  canSign: boolean;
  declarationText: string;
  onSign: () => void;
  signing: boolean;
}) {
  const icon = type === 'Employee' ? <FileSignature className="w-5 h-5" /> : <Shield className="w-5 h-5" />;

  return (
    <div className={`border rounded-xl p-4 ${declaration?.signed ? 'border-primary/20 bg-primary/5' : canSign ? 'border-primary/40' : 'border-border'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={declaration?.signed ? 'text-primary' : 'text-muted-foreground'}>{icon}</span>
        <h3 className="text-sm font-bold text-foreground">{type} Declaration</h3>
        {declaration?.signed && <CheckCircle className="w-4 h-4 text-primary ml-auto" />}
      </div>

      <p className="text-xs text-muted-foreground mb-3 italic">&quot;{declarationText}&quot;</p>

      {declaration?.signed ? (
        <div className="text-xs text-muted-foreground">
          Signed by <strong className="text-foreground">{declaration.signerName}</strong> on{' '}
          {new Date(declaration.signedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
      ) : canSign ? (
        <Button onClick={onSign} disabled={signing} className="w-full h-10 text-sm touch-manipulation">
          {signing ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Signing...</> : `Sign ${type} Declaration`}
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          {type === 'Employee' ? 'Complete all modules first' : 'Employee must sign first'}
        </p>
      )}
    </div>
  );
}

export default function CourseCompletion() {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const { user } = useAuth();
  const [data, setData] = useState<Data | null>(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = () => {
    if (!user || !enrollmentId) return;
    getCourseCompletion({ enrollmentId }).then(setData).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user, enrollmentId]);

  const handleSign = async (type: 'Employee' | 'Manager') => {
    if (!enrollmentId) return;
    setSigning(true);
    try {
      const result = await signDeclaration({ enrollmentId, declarationType: type });
      toast.success(result.message);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to sign');
    } finally {
      setSigning(false);
    }
  };

  const handleGenerateCert = async () => {
    if (!enrollmentId) return;
    setGenerating(true);
    try {
      const result = await generateTrainingCertificate({ enrollmentId });
      toast.success(result.message);
      load();
    } catch (e: any) {
      toast.error(e.message || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-6 w-48" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" />
    </div>
  );

  if (!data) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center">
      <AlertTriangle className="w-10 h-10 text-destructive mx-auto mb-3" />
      <p className="text-sm text-muted-foreground">Could not load completion data</p>
      <Link to="/train" className="text-sm text-primary hover:underline mt-2 inline-block">← Back</Link>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto pb-8">
      {/* Header */}
      <div className="px-4 py-4 border-b border-border bg-card">
        <Link to={`/train/${enrollmentId}`} className="text-xs text-muted-foreground flex items-center gap-1 mb-2 hover:text-foreground">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Course
        </Link>
        <h1 className="text-lg font-bold text-foreground">Course Completion</h1>
        <p className="text-sm text-muted-foreground">{data.course.title} · {data.employee.name}</p>
      </div>

      {/* Open corrective actions warning */}
      {data.openCorrectiveActions > 0 && (
        <div className="mx-4 mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-xl flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-xs text-destructive">{data.openCorrectiveActions} open corrective action{data.openCorrectiveActions > 1 ? 's' : ''} must be resolved before declarations can be signed.</p>
        </div>
      )}

      {/* Module Completion Checklist */}
      <div className="px-4 pt-4">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Module Checklist</h2>
        <div className="space-y-2">
          {data.checklist.map((item, i) => <ChecklistItem key={i} item={item} />)}
        </div>
        {!data.allChecklistComplete && (
          <p className="text-xs text-muted-foreground mt-2 italic">All modules must have approved practical assessments to proceed.</p>
        )}
      </div>

      {/* Declarations */}
      <div className="px-4 pt-6">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Declarations</h2>
        <div className="space-y-3">
          <DeclarationCard
            type="Employee"
            declaration={data.declarations.employee}
            canSign={data.canSignEmployee}
            declarationText={data.course.employeeDeclarationText}
            onSign={() => handleSign('Employee')}
            signing={signing}
          />
          <DeclarationCard
            type="Manager"
            declaration={data.declarations.manager}
            canSign={data.canSignManager}
            declarationText={data.course.managerDeclarationText}
            onSign={() => handleSign('Manager')}
            signing={signing}
          />
        </div>
      </div>

      {/* Certificate */}
      <div className="px-4 pt-6">
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Certificate</h2>
        {data.certificate ? (
          <div className="border-2 border-primary/20 bg-primary/5 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-3">
              <Award className="w-8 h-8 text-primary" />
              <div>
                <p className="text-sm font-bold text-foreground">Certificate Issued</p>
                <p className="text-xs text-muted-foreground font-mono">Verification: {data.certificate.verificationCode}</p>
              </div>
            </div>
            <a href={data.certificate.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-semibold touch-manipulation active:scale-[0.98] transition-transform">
              <Download className="w-4 h-4" /> Download Certificate
            </a>
          </div>
        ) : data.canGenerateCertificate ? (
          <Button onClick={handleGenerateCert} disabled={generating} className="w-full h-12 text-sm font-semibold touch-manipulation">
            {generating ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Generating...</> : <><Award className="w-4 h-4 mr-1.5" /> Generate Certificate</>}
          </Button>
        ) : (
          <div className="border-2 border-dashed border-border rounded-xl p-4 flex items-center gap-3">
            <Award className="w-8 h-8 text-muted-foreground" />
            <div>
              <p className="text-sm font-semibold text-foreground">Certificate</p>
              <p className="text-xs text-muted-foreground">Both declarations must be signed before the certificate can be generated.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
